'use client';

import { useCallback, useMemo, useState, useEffect, ReactNode } from 'react';
import {
  cognitoSignOut,
  userManager,
  CognitoUser,
  CognitoAuthContext,
  getCustomLoginURL,
  CUSTOM_LOGIN_URL,
} from './shared/lib/contexts/cognito';

export type CognitoAuthProviderProps = {
  children: ReactNode;
};

export default function CognitoAuthProvider({
  children,
}: CognitoAuthProviderProps) {
  const [user, setUser] = useState<CognitoUser>(null);

  const signIn = async () => {
    if (CUSTOM_LOGIN_URL) {
      window.location.href = getCustomLoginURL();
    } else {
      await userManager.signinRedirect();
    }
  };

  const applyCognitoSession = useCallback(async (cognitoUser?: CognitoUser) => {
    if (!cognitoUser) {
      setUser(null);
      return;
    }

    setUser(cognitoUser);
  }, []);

  const getUser = async () => {
    const cognitoUser = await userManager.getUser();
    await applyCognitoSession(cognitoUser);
  };

  const handleSignInCallback = async (cognitoUser?: CognitoUser) => {
    await applyCognitoSession(cognitoUser);
  };

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    const handleUserLoaded = (cognitoUser: CognitoUser) => {
      applyCognitoSession(cognitoUser);
    };

    const handleAccessTokenExpiring = () => {
      userManager.signinSilent().catch((error) => {
        console.error('Error during silent renew (expiring):', error);
      });
    };

    const handleAccessTokenExpired = () => {
      userManager.signinSilent().catch((error) => {
        console.error('Error during silent renew (expired):', error);
      });
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [applyCognitoSession]);

  const values = useMemo(
    () => ({
      user,
      signIn,
      signOut: cognitoSignOut,
      clearUser: () => setUser(null),
      getUser,
      handleSignInCallback,
    }),
    [user, getUser, handleSignInCallback, signIn],
  );
  return (
    <CognitoAuthContext.Provider value={values}>
      {children}
    </CognitoAuthContext.Provider>
  );
}
